import React from "react"
import { Checkbox, Select } from "antd"
import { sameSummary, summaryName } from "../../model/summaries"
import type { SummaryChoice } from "../../types/tabSpecDocument"

/** A summary as one checkbox: "top-2", "bottom-2", "answer", or its kind. */
export const summaryKey = (choice: SummaryChoice): string =>
  choice.kind === "top" || choice.kind === "bottom" ? `${choice.kind}-${choice.size}` : choice.kind

/** The summaries the checkboxes offer, in their order. One answer comes with a list of answers. */
export const SUMMARY_OPTIONS: SummaryChoice[] = [
  { kind: "top", size: 2 },
  { kind: "bottom", size: 2 },
  { kind: "middle" },
  { kind: "mean" },
  { kind: "nps" },
  { kind: "sum" },
]

export interface SummaryCheckboxesProps {
  /** The summaries offered, in order; one answer is offered when `answers` is given. */
  options: SummaryChoice[]
  value: SummaryChoice[]
  onChange: (next: SummaryChoice[]) => void
  /** The answers one answer can be (label, and the name it is shown under); absent: no one answer. */
  answers?: { value: string; label: string }[]
  /** Names the list for screen readers. */
  label: string
}

/**
 * A checkbox per summary a grid can get, in the order of `options`, each
 * named as the titles name it ("Top 2", "Mean"); one answer comes with the
 * answer it shows. The ticked ones are written in the order of the options.
 * A ticked summary that is not offered (from a stored list) stays listed,
 * so it can be unticked.
 */
const SummaryCheckboxes: React.FC<SummaryCheckboxesProps> = ({
  options,
  value,
  onChange,
  answers,
  label,
}) => {
  const answer = value.find((c) => c.kind === "answer")
  const shown = [
    ...options,
    ...value.filter((c) => c.kind !== "answer" && !options.some((o) => sameSummary(o, c))),
  ]
  /** The ticked ones in the order of the list, one answer last. */
  const write = (keys: string[], answerLabel: string | null): void =>
    onChange([
      ...shown.filter((c) => keys.includes(summaryKey(c))),
      ...(answerLabel !== null ? [{ kind: "answer" as const, label: answerLabel }] : []),
    ])
  const keys = value.filter((c) => c.kind !== "answer").map(summaryKey)
  const firstAnswer = answers?.[0]?.value ?? null
  return (
    <div className="ts-summaries" role="group" aria-label={label}>
      <Checkbox.Group
        options={shown.map((c) => ({ value: summaryKey(c), label: summaryName(c) }))}
        value={keys}
        onChange={(next) => write(next as string[], answer?.kind === "answer" ? answer.label : null)}
      />
      {answers && answers.length > 0 && (
        <div className="ts-field__row">
          <Checkbox
            checked={answer !== undefined}
            onChange={(e) => write(keys, e.target.checked ? firstAnswer : null)}
          >
            One answer
          </Checkbox>
          {answer?.kind === "answer" && (
            <Select
              size="small"
              aria-label="Answer"
              className="ts-field__select"
              value={answer.label}
              options={answers}
              onChange={(l: string) => write(keys, l)}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default SummaryCheckboxes
